import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import moment from "moment";
import dayjs from "dayjs";
import {
  FaClipboardList,
  FaTimes,
  FaGripHorizontal,
  FaEye,
} from "react-icons/fa";
import styles from "./Editmodal.module.css";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import {
  fetchCategoryOptions,
  fetchClassOptions,
  fetchCountryOptions,
  fetchOccupationOptions,
  fetchReligionOptions,
  fetchStateOptions,
  GENDER_OPTIONS,
  STATE_TYPES,
} from "../../../../redux/Masterdata";
import {
  CustomButton,
  CustomDatePicker,
  CustomDropdown,
  CustomFileInput,
  CustomRadioGroup,
  CustomTextInput,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import {
  switchFormLanguage,
  resetFormLanguage,
} from "../../../../redux/languageSlice";

// ==================== DATE TO WORDS CONVERTER ====================
const numberToWords = (num) => {
  const ones = [
    "",
    "First",
    "Second",
    "Third",
    "Fourth",
    "Fifth",
    "Sixth",
    "Seventh",
    "Eighth",
    "Ninth",
    "Tenth",
    "Eleventh",
    "Twelfth",
    "Thirteenth",
    "Fourteenth",
    "Fifteenth",
    "Sixteenth",
    "Seventeenth",
    "Eighteenth",
    "Nineteenth",
  ];
  const tens = [
    "",
    "",
    "Twentieth",
    "Thirtieth",
    "Fortieth",
    "Fiftieth",
    "Sixtieth",
    "Seventieth",
    "Eightieth",
    "Ninetieth",
  ];
  const tensPrefix = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  if (num <= 0) return "";
  if (num < 20) return ones[num];
  if (num % 10 === 0) return tens[Math.floor(num / 10)];
  return tensPrefix[Math.floor(num / 10)] + "-" + ones[num % 10].toLowerCase();
};

const yearToWords = (year) => {
  const onesW = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tensW = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const convertTwoDigit = (n) => {
    if (n === 0) return "";
    if (n < 20) return onesW[n];
    return (
      tensW[Math.floor(n / 10)] +
      (n % 10 ? "-" + onesW[n % 10].toLowerCase() : "")
    );
  };
  if (year >= 2000) {
    const remainder = year - 2000;
    if (remainder === 0) return "Two Thousand";
    if (remainder < 10) return "Two Thousand and " + onesW[remainder];
    return "Two Thousand and " + convertTwoDigit(remainder);
  }
  const first = Math.floor(year / 100);
  const last = year % 100;
  return (
    convertTwoDigit(first) +
    " Hundred" +
    (last > 0 ? " and " + convertTwoDigit(last) : "")
  );
};

const convertDateToWords = (dateStr) => {
  if (!dateStr) return "";
  const d = dayjs(dateStr);
  if (!d.isValid()) return "";
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${numberToWords(d.date())} ${monthNames[d.month()]} ${yearToWords(d.year())}`;
};

// ==================== STATIC OPTIONS ====================
const LIVING_WITH_OPTIONS = [
  { value: "", label: "Select" },
  { value: "parent", label: "Parent" },
  { value: "guardian", label: "Guardian" },
];

const MEDIUM_OPTIONS = [
  { value: "TM", label: "TM", disabled: false },
  { value: "EM", label: "EM", disabled: false },
];

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes", disabled: false },
  { value: "No", label: "No", disabled: false },
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: "TC", label: "TC", disabled: false },
  { value: "Record Sheet", label: "Record Sheet", disabled: false },
];

const MOTHER_TONGUE_OPTIONS = [
  { value: "", label: "Select Mother Tongue" },
  { value: "Tamil", label: "Tamil" },
  { value: "English", label: "English" },
  { value: "Hindi", label: "Hindi" },
  { value: "Telugu", label: "Telugu" },
  { value: "Malayalam", label: "Malayalam" },
  { value: "Kannada", label: "Kannada" },
  { value: "Urdu", label: "Urdu" },
  { value: "Others", label: "Others" },
];

const LANGUAGE_OPTIONS = [
  { value: "", label: "Select" },
  { value: "Tamil", label: "Tamil" },
  { value: "English", label: "English" },
  { value: "Hindi", label: "Hindi" },
  { value: "Telugu", label: "Telugu" },
  { value: "Malayalam", label: "Malayalam" },
  { value: "Kannada", label: "Kannada" },
  { value: "Urdu", label: "Urdu" },
  { value: "French", label: "French" },
  { value: "Sanskrit", label: "Sanskrit" },
];

// ==================== TAMIL KEYBOARD LAYOUT ====================
const TAMIL_VOWELS = [
  "அ",
  "ஆ",
  "இ",
  "ஈ",
  "உ",
  "ஊ",
  "எ",
  "ஏ",
  "ஐ",
  "ஒ",
  "ஓ",
  "ஔ",
];
const TAMIL_CONSONANTS = [
  "க",
  "ங",
  "ச",
  "ஞ",
  "ட",
  "ண",
  "த",
  "ந",
  "ப",
  "ம",
  "ய",
  "ர",
  "ல",
  "வ",
  "ழ",
  "ள",
  "ற",
  "ன",
  "ஜ",
  "ஷ",
  "ஸ",
  "ஹ",
];
const TAMIL_MODIFIERS = [
  "்",
  "ா",
  "ி",
  "ீ",
  "ு",
  "ூ",
  "ெ",
  "ே",
  "ை",
  "ொ",
  "ோ",
  "ௌ",
];

// ==================== YEAR OPTIONS ====================
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [{ value: "", label: "Select Year" }];
  for (let y = currentYear; y >= currentYear - 20; y--) {
    years.push({ value: y.toString(), label: y.toString() });
  }
  return years;
};
const YEAR_OPTIONS = generateYearOptions();

// ==================== TAMIL ↔ ENGLISH VALUE MAP ====================
const TAMIL_ENGLISH_VALUE_MAP = {
  // Living with
  பெற்றோர்: "parent",
  பெற்றோர: "parent",
  Parent: "parent",
  parent: "parent",
  பாதுகாவலர்: "guardian",
  காப்பாளர்: "guardian",
  Guardian: "guardian",
  guardian: "guardian",
  // Yes / No
  ஆம்: "Yes",
  ஆம: "Yes",
  இல்லை: "No",
  Yes: "Yes",
  No: "No",
  // Gender
  ஆண்: "Male",
  ஆண: "Male",
  பெண்: "Female",
  பெண: "Female",
  மற்றவை: "Other",
  Male: "Male",
  Female: "Female",
  Other: "Other",
  // Medium
  TM: "TM",
  EM: "EM",
  "Tamil Medium": "TM",
  "English Medium": "EM",
  "தமிழ் வழி": "TM",
  "ஆங்கில வழி": "EM",
  தமிழ்வழி: "TM",
  ஆங்கிலவழி: "EM",
  // Document type
  TC: "TC",
  "Record Sheet": "Record Sheet",
  "மாற்றுச் சான்றிதழ்": "TC",
  "பதிவு தாள்": "Record Sheet",
  // Mother tongue / Languages
  தமிழ்: "Tamil",
  ஆங்கிலம்: "English",
  இந்தி: "Hindi",
  தெலுங்கு: "Telugu",
  மலையாளம்: "Malayalam",
  கன்னடம்: "Kannada",
  உருது: "Urdu",
  பிரெஞ்சு: "French",
  சமஸ்கிருதம்: "Sanskrit",
  Tamil: "Tamil",
  English: "English",
  Hindi: "Hindi",
  Telugu: "Telugu",
  Malayalam: "Malayalam",
  Kannada: "Kannada",
  Urdu: "Urdu",
  French: "French",
  Sanskrit: "Sanskrit",
  Others: "Others",
  மற்றவை: "Others",
  // Religion
  இந்து: "Hindu",
  Hindu: "Hindu",
  கிறிஸ்தவம்: "Christian",
  கிருத்துவம்: "Christian",
  Christian: "Christian",
  இஸ்லாம்: "Muslim",
  முஸ்லிம்: "Muslim",
  Muslim: "Muslim",
  சீக்கியம்: "Sikh",
  Sikh: "Sikh",
  புத்தம்: "Buddhist",
  Buddhist: "Buddhist",
  சமணம்: "Jain",
  Jain: "Jain",
  // Category / Community
  OC: "OC",
  BC: "BC",
  BCM: "BCM",
  MBC: "MBC",
  DNC: "DNC",
  SC: "SC",
  SCA: "SCA",
  ST: "ST",
  பிற்படுத்தப்பட்டோர்: "BC",
  "மிகவும் பிற்படுத்தப்பட்டோர்": "MBC",
  "பட்டியல் சாதி": "SC",
  "பட்டியல் பழங்குடி": "ST",
  // Nationality
  Indian: "Indian",
  இந்தியன்: "Indian",
  இந்தியா: "Indian",
  // Common state names (Tamil → English) for matching
  தமிழ்நாடு: "Tamil Nadu",
  "Tamil Nadu": "Tamil Nadu",
  கேரளா: "Kerala",
  Kerala: "Kerala",
  கர்நாடகா: "Karnataka",
  Karnataka: "Karnataka",
  "ஆந்திரப் பிரதேசம்": "Andhra Pradesh",
  "Andhra Pradesh": "Andhra Pradesh",
  தெலங்கானா: "Telangana",
  Telangana: "Telangana",
  மகாராஷ்டிரா: "Maharashtra",
  Maharashtra: "Maharashtra",
  "உத்தரப் பிரதேசம்": "Uttar Pradesh",
  "Uttar Pradesh": "Uttar Pradesh",
  "மத்தியப் பிரதேசம்": "Madhya Pradesh",
  "Madhya Pradesh": "Madhya Pradesh",
  குஜராத்: "Gujarat",
  Gujarat: "Gujarat",
  ராஜஸ்தான்: "Rajasthan",
  Rajasthan: "Rajasthan",
  பஞ்சாப்: "Punjab",
  Punjab: "Punjab",
  "மேற்கு வங்காளம்": "West Bengal",
  "West Bengal": "West Bengal",
  பீகார்: "Bihar",
  Bihar: "Bihar",
  ஒடிசா: "Odisha",
  Odisha: "Odisha",
  அசாம்: "Assam",
  Assam: "Assam",
  ஜார்கண்ட்: "Jharkhand",
  Jharkhand: "Jharkhand",
  சத்தீஸ்கர்: "Chhattisgarh",
  Chhattisgarh: "Chhattisgarh",
  ஹரியானா: "Haryana",
  Haryana: "Haryana",
  உத்தரகண்ட்: "Uttarakhand",
  Uttarakhand: "Uttarakhand",
  "ஹிமாச்சலப் பிரதேசம்": "Himachal Pradesh",
  "Himachal Pradesh": "Himachal Pradesh",
  "ஜம்மு காஷ்மீர்": "Jammu and Kashmir",
  "Jammu and Kashmir": "Jammu and Kashmir",
  கோவா: "Goa",
  Goa: "Goa",
  டெல்லி: "Delhi",
  Delhi: "Delhi",
  புதுச்சேரி: "Puducherry",
  Puducherry: "Puducherry",
};

/**
 * Normalize a value against specific options (for radio buttons & dropdowns).
 */
const normalizeValueAgainstOptions = (val, options) => {
  if (!val) return "";
  const result = findMatchInOptions(val, options);
  return result;
};

/**
 * Try to find a matching option in an options array for a raw value.
 */
const findMatchInOptions = (rawVal, options, matchBy = "value") => {
  if (!rawVal || !options || options.length === 0) return "";
  const strVal = String(rawVal).trim();
  if (!strVal) return "";

  const mapped = TAMIL_ENGLISH_VALUE_MAP[strVal];
  if (mapped) {
    const opt = options.find(
      (o) =>
        o.value &&
        (String(o[matchBy]) === mapped ||
          String(o[matchBy]).toLowerCase() === mapped.toLowerCase() ||
          (o.label && o.label.toLowerCase() === mapped.toLowerCase())),
    );
    if (opt) return opt[matchBy];
  }

  const exactMatch = options.find(
    (o) => o[matchBy] && String(o[matchBy]) === strVal,
  );
  if (exactMatch) return exactMatch[matchBy];

  const lowerVal = strVal.toLowerCase();
  const exactLabel = options.find(
    (o) => o[matchBy] && String(o[matchBy]).toLowerCase() === lowerVal,
  );
  if (exactLabel) return exactLabel[matchBy];

  if (matchBy === "value") {
    const labelMatch = options.find(
      (o) => o.label && o.label.toLowerCase() === lowerVal,
    );
    if (labelMatch) return labelMatch[matchBy];
  }

  if (matchBy === "label") {
    const valueMatch = options.find(
      (o) => o.value && String(o.value).toLowerCase() === lowerVal,
    );
    if (valueMatch) return valueMatch[matchBy];
  }

  const containsMatch = options.find(
    (o) =>
      o[matchBy] &&
      o.value &&
      (String(o[matchBy]).toLowerCase().includes(lowerVal) ||
        lowerVal.includes(String(o[matchBy]).toLowerCase())),
  );
  if (containsMatch) return containsMatch[matchBy];

  if (matchBy === "value") {
    const labelContains = options.find(
      (o) =>
        o.label &&
        o.value &&
        (o.label.toLowerCase().includes(lowerVal) ||
          lowerVal.includes(o.label.toLowerCase())),
    );
    if (labelContains) return labelContains[matchBy];
  }

  return "";
};

// ==================== EDIT FORM VALIDATION ====================
const validateSecondaryEditForm = (formData, translations, isSeatAllocate) => {
  const errors = {};
  const t = translations?.labels || {};

  const getVal = (field) => {
    if (!field && field !== 0) return "";
    if (typeof field === "object" && field.value !== undefined)
      return field.value;
    return field;
  };

  // ── Basic Details ──
  if (!getVal(formData.student_name_in_tamil)?.trim()) {
    errors.student_name_in_tamil =
      t?.studentNameTamilRequired || "Student's Name (Tamil) is required";
  }

  if (!getVal(formData.student_name)?.trim()) {
    errors.student_name =
      t?.studentNameEnglishRequired || "Student's Name (English) is required";
  }

  if (!getVal(formData.date_of_birth)) {
    errors.date_of_birth =
      t?.dateOfBirthRequired || "Date of birth is required";
  }

  if (!getVal(formData.email)?.trim()) {
    errors.email = t?.emailRequired || "Email is required";
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(getVal(formData.email).trim())) {
      errors.email = t?.emailInvalid || "Enter a valid email address";
    }
  }

  if (!getVal(formData.mobile_number)?.trim()) {
    errors.mobile_number =
      t?.mobileNumberRequired || "Mobile number is required";
  } else if (!/^[6-9]\d{9}$/.test(getVal(formData.mobile_number).trim())) {
    errors.mobile_number =
      t?.mobileNumberInvalid || "Enter valid 10-digit mobile number";
  }

  if (!getVal(formData.gender)) {
    errors.gender = t?.genderRequired || "Gender is required";
  }

  if (!getVal(formData.religion)) {
    errors.religion = t?.religionRequired || "Religion is required";
  }

  if (!getVal(formData.caste)?.trim()) {
    errors.caste = t?.casteRequired || "Caste is required";
  }

  if (!getVal(formData.community)) {
    errors.community = t?.categoryRequired || "Category is required";
  }

  if (!getVal(formData.nationality)?.trim()) {
    errors.nationality = t?.nationalityRequired || "Nationality is required";
  }

  if (!getVal(formData.student_native_state)) {
    errors.student_native_state = t?.stateRequired || "State is required";
  }

  // ── Residential Details ──
  const livingWith = getVal(formData.living_with);
  if (!livingWith) {
    errors.living_with = t?.livingWithRequired || "This field is required";
  }

  if (livingWith) {
    const isGuardian = livingWith === "guardian";
    const nameField = isGuardian ? "guardian_name" : "parent_name";
    const occField = isGuardian ? "guardian_occupation" : "parent_occupation";
    const salaryField = isGuardian
      ? "guardian_annual_salary"
      : "parent_annual_salary";
    const addressField = isGuardian ? "guardian_address" : "parent_address";
    const cityField = isGuardian ? "guardian_city" : "parent_city";
    const districtField = isGuardian ? "guardian_district" : "parent_district";
    const stateField = isGuardian ? "guardian_state" : "parent_state";
    const pincodeField = isGuardian ? "guardian_pincode" : "parent_pincode";

    if (!getVal(formData[nameField])?.trim()) {
      errors[nameField] = isGuardian
        ? t?.guardianNameRequired || "Name is required"
        : t?.parentNameRequired || "Name is required";
    }

    if (!getVal(formData[occField])) {
      errors[occField] = t?.occupationRequired || "Occupation is required";
    }

    if (!getVal(formData[salaryField])?.toString().trim()) {
      errors[salaryField] = t?.incomeRequired || "Income is required";
    }

    if (!getVal(formData[addressField])?.trim()) {
      errors[addressField] = t?.addressRequired || "Address is required";
    }

    if (!getVal(formData[cityField])?.trim()) {
      errors[cityField] = t?.cityRequired || "City is required";
    }

    if (!getVal(formData[districtField])?.trim()) {
      errors[districtField] = t?.districtRequired || "District is required";
    }

    if (!getVal(formData[stateField])) {
      errors[stateField] = t?.stateRequired || "State is required";
    }

    if (!getVal(formData[pincodeField])?.trim()) {
      errors[pincodeField] = t?.pincodeRequired || "Pincode is required";
    } else if (!/^\d{6}$/.test(getVal(formData[pincodeField]).trim())) {
      errors[pincodeField] = t?.pincodeInvalid || "Enter valid 6-digit pincode";
    }
  }

  // ── Last Studied Details ──
  if (!getVal(formData.last_studied_class)) {
    errors.last_studied_class =
      t?.lastStudiedClassRequired || "Last Studied Class is required";
  }

  if (!getVal(formData.last_studied_school)?.trim()) {
    errors.last_studied_school =
      t?.lastStudiedSchoolRequired || "Last Studied School is required";
  }

  if (!getVal(formData.emis_id)?.trim()) {
    errors.emis_id = t?.emisNoRequired || "EMIS No is required";
  } else if (!/^\d{10}$/.test(getVal(formData.emis_id).trim())) {
    errors.emis_id = t?.emisNoInvalid || "Enter a valid EMIS No";
  }

  if (!getVal(formData.last_studied_medium)) {
    errors.last_studied_medium =
      t?.lastStudiedMediumRequired || "Last Studied Medium is required";
  }

  if (!getVal(formData.document_path)) {
    errors.document_path = t?.documentRequired || "Document is required";
  }

  // ── Preferred Course Details ──
  if (!getVal(formData.preferred_class)) {
    errors.preferred_class =
      t?.preferredClassRequired || "Preferred Class for Admission is required";
  }

  if (!getVal(formData.mother_tongue)) {
    errors.mother_tongue =
      t?.motherTongueRequired || "Mother Tongue is required";
  }

  if (!getVal(formData.preferred_first_language)) {
    errors.preferred_first_language =
      t?.preferredFirstLanguageRequired ||
      "Preferred First Language to Study is required";
  }

  if (!getVal(formData.preferred_stream)) {
    errors.preferred_stream =
      t?.preferredMediumRequired || "Preferred Medium is required";
  }

  // ── Previous School Address (Row 1 required) ──
  if (!getVal(formData.previous_school_address_1)?.trim()) {
    errors.previous_school_address_1 =
      t?.schoolNameRequired || "School Name is required";
  }

  if (!getVal(formData.previous_studied_class_1)) {
    errors.previous_studied_class_1 = t?.classRequired || "Class is required";
  }

  if (!getVal(formData.previous_studied_year_1)) {
    errors.previous_studied_year_1 =
      t?.yearOfStudyRequired || "Year of Study is required";
  }

  // ── Identification Marks ──
  if (!getVal(formData.identification_mark_1)?.trim()) {
    errors.identification_mark_1 =
      t?.identificationMark1Required || "Identification Mark 1 is required";
  }

  if (!getVal(formData.identification_mark_2)?.trim()) {
    errors.identification_mark_2 =
      t?.identificationMark2Required || "Identification Mark 2 is required";
  }

  // ── Other Details ──
  if (!getVal(formData.place)?.trim()) {
    errors.place = t?.placeRequired || "Place is required";
  }

  if (!getVal(formData.student_image)) {
    errors.student_image =
      t?.studentImageRequired || "Student Image is required";
  }

  // ── Assigned Class & Section validation (only when status is "Seat Allocated" and isSeatAllocate === 1) ──
  const selectedStatus = formData.status?.value
    ? formData.status.value
    : getVal(formData.status);

  if (selectedStatus === "Seat Allocated" && isSeatAllocate === 1) {
    if (!getVal(formData.assigned_class_id)) {
      errors.assigned_class_id =
        t?.assignedClassRequired || "Assigned Class is required";
    }
    if (!getVal(formData.assigned_section)) {
      errors.assigned_section =
        t?.assignedSectionRequired || "Assigned Section is required";
    }
  }

  // ── Feedback required when status is Rejected or Approved ──
  const selectedStatusVal = formData.status?.value
    ? formData.status.value
    : getVal(formData.status);

  if (selectedStatusVal === "Rejected" || selectedStatusVal === "Approved") {
    if (!getVal(formData.feedback)?.trim()) {
      errors.feedback =
        translations?.labels?.feedbackRequired || "Feedback is required";
    }
  }

  return errors;
};

// ==================== INITIAL FORM DATA ====================
const INITIAL_FORM_DATA = {
  assigned_section: null,
  assigned_class: null,
  assigned_class_id: null,
  date: moment().format("YYYY-MM-DD"),
  student_name_in_tamil: "",
  student_name: "",
  date_of_birth: null,
  date_of_birth_words: "",
  aadhar_no: "",
  email: "",
  mobile_number: "",
  gender: "",
  religion: "",
  caste: "",
  community: "",
  nationality: "Indian",
  student_native_state: "",
  living_with: "",
  parent_name: "",
  parent_occupation: "",
  parent_annual_salary: "",
  parent_address: "",
  parent_city: "",
  parent_district: "",
  parent_state: "",
  parent_pincode: "",
  guardian_name: "",
  guardian_occupation: "",
  guardian_annual_salary: "",
  guardian_address: "",
  guardian_city: "",
  guardian_district: "",
  guardian_state: "",
  guardian_pincode: "",
  last_studied_class: "",
  last_studied_school: "",
  emis_id: "",
  last_studied_medium: "TM",
  qualified_higher_secondary: "No",
  attached_document_type: "TC",
  document_path: "",
  document_original_name: "",
  preferred_class: "",
  mother_tongue: "",
  preferred_first_language: "",
  preferred_stream: "TM",
  is_vaccinated_pox: "Yes",
  previous_school_address_1: "",
  previous_studied_class_1: "",
  previous_studied_medium_1: "TM",
  previous_studied_year_1: "",
  previous_school_address_2: "",
  previous_studied_class_2: "",
  previous_studied_medium_2: "TM",
  previous_studied_year_2: "",
  previous_school_address_3: "",
  previous_studied_class_3: "",
  previous_studied_medium_3: "TM",
  previous_studied_year_3: "",
  identification_mark_1: "",
  identification_mark_2: "",
  place: "",
  student_image: "",
  student_image_original_name: "",
  current_status: "",
  status: "",
  feedback: "",
  ocr_upload_path: "",
};

const NUMBER_ONLY_FIELDS = [
  "mobile_number",
  "aadhar_no",
  "parent_pincode",
  "guardian_pincode",
  "emis_id",
  "parent_annual_salary",
  "guardian_annual_salary",
];

// ==================== COMPONENT ====================
const SecondaryAdmissionEditModal = ({
  open,
  onClose,
  admissionId,
  fetchAdmissionList,
  realAdmissionId,
}) => {
  const dispatch = useDispatch();
  const { translations, formTranslations, formLanguage, language } =
    useSelector((state) => state.language);
  const activeTranslations = formTranslations || translations;
  const t = useMemo(
    () => activeTranslations?.labels || {},
    [activeTranslations],
  );

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const user_id = user?.id || "";

  // Redux master data
  const {
    occupationOptions: reduxOccupationOptions,
    religionOptions: reduxReligionOptions,
    categoryOptions: reduxCategoryOptions,
    fatherStateOptions: reduxFatherStateOptions,
    classOptions: reduxClassOptions,
  } = useSelector((state) => state.masterData);

  // Local state
  const [formData, setFormData] = useState({ ...INITIAL_FORM_DATA });
  const [isSeatAllocate, setIsSeatAllocate] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
  });

  // Tamil keyboard
  const [showTamilKeyboard, setShowTamilKeyboard] = useState(false);
  const [activeInputName, setActiveInputName] = useState("");

  // Draggable keyboard
  const [kbPosition, setKbPosition] = useState({ x: 100, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const keyboardRef = useRef(null);

  // ---- Deferred dropdown normalization refs ----
  const rawApiDataRef = useRef(null);
  const normalizedOnceRef = useRef(false);

  // ==================== DRAG HANDLERS ====================
  const handleDragStart = useCallback(
    (e) => {
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      dragOffset.current = {
        x: clientX - kbPosition.x,
        y: clientY - kbPosition.y,
      };
      setIsDragging(true);
    },
    [kbPosition],
  );

  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setKbPosition({
        x: Math.max(
          0,
          Math.min(clientX - dragOffset.current.x, window.innerWidth - 50),
        ),
        y: Math.max(
          0,
          Math.min(clientY - dragOffset.current.y, window.innerHeight - 50),
        ),
      });
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);
    window.addEventListener("touchmove", handleDragMove, { passive: false });
    window.addEventListener("touchend", handleDragEnd);
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // ==================== DROPDOWN OPTIONS (memoized) ====================
  const [sectionOptions, setSectionOptions] = useState([]);
  const genderOptions = useMemo(
    () =>
      GENDER_OPTIONS.map((opt) => ({
        ...opt,
        label: t?.[opt.label?.toLowerCase()] || opt.label,
      })),
    [t],
  );
  const religionOptions = useMemo(
    () => [
      { value: "", label: t?.selectReligion || "Select Religion" },
      ...reduxReligionOptions,
    ],
    [reduxReligionOptions, t],
  );
  const categoryOptions = useMemo(
    () => [
      { value: "", label: t?.selectCategory || "Select Category" },
      ...reduxCategoryOptions,
    ],
    [reduxCategoryOptions, t],
  );
  const stateOptions = useMemo(
    () => [
      { value: "", label: t?.selectState || "Select State" },
      ...reduxFatherStateOptions,
    ],
    [reduxFatherStateOptions, t],
  );
  const occupationOptions = useMemo(
    () => [
      { value: "", label: t?.selectOccupation || "Select Occupation" },
      ...reduxOccupationOptions,
    ],
    [reduxOccupationOptions, t],
  );
  const classOptions = useMemo(() => {
    const filteredClasses = reduxClassOptions.filter(
      (item) => item.label !== "XI" && item.label !== "XII",
    );
    return [
      { value: "", label: t?.selectClass || "Select Class" },
      ...filteredClasses,
    ];
  }, [reduxClassOptions, t]);

  // ===== Assigned Class options — only show the preferred_class match =====
  const assignedClassOptions = useMemo(() => {
    const defaultOption = [
      { value: "", label: t?.selectClass || "Select Class" },
    ];

    const preferredVal = formData.preferred_class;
    if (!preferredVal) return defaultOption;

    const preferredStr = String(preferredVal).trim();
    const matchedClass = reduxClassOptions.find(
      (opt) =>
        String(opt.value) === preferredStr ||
        (opt.label && opt.label.toLowerCase() === preferredStr.toLowerCase()),
    );

    return matchedClass ? [...defaultOption, matchedClass] : defaultOption;
  }, [reduxClassOptions, formData.preferred_class, t]);

  // ===== Assigned Section options (from fetchSectionList) =====
  const assignedSectionOptions = useMemo(() => {
    return [
      { value: "", label: t?.selectSection || "Select Section" },
      ...sectionOptions,
    ];
  }, [sectionOptions, t]);

  const livingWithOptions = useMemo(
    () =>
      LIVING_WITH_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.select || "Select"
            : opt.value === "parent"
              ? t?.withParents || "With Parents"
              : t?.withGuardian || "With Guardian",
      })),
    [t],
  );
  const motherTongueOptions = useMemo(
    () =>
      MOTHER_TONGUE_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t?.selectMotherTongue || "Select Mother Tongue"
            : opt.label,
      })),
    [t],
  );
  const languageOptions = useMemo(
    () =>
      LANGUAGE_OPTIONS.map((opt) => ({
        ...opt,
        label: opt.value === "" ? t?.select || "Select" : opt.label,
      })),
    [t],
  );

  // ===== Derived: should we show the Assigned Class/Section dropdowns? =====
  const showSeatAllocationFields =
    formData.current_status === "Proceed" && isSeatAllocate === 1;

  // ==================== STATUS DROPDOWN HELPERS ====================
  const getStatusOptions = (currentStatus) => {
    const baseOptions = [{ value: "", label: t?.select || "Select" }];

    if (!currentStatus || currentStatus === "") {
      return baseOptions;
    }

    const statusMap = {
      Preview: [
        { value: "Submitted", label: "Submitted" },
        { value: "Rejected", label: t?.reject || "Rejected" },
      ],
      Submitted: [
        { value: "Proceed", label: t?.proceed || "Proceed" },
        { value: "Rejected", label: t?.reject || "Rejected" },
      ],
      Proceed:
        isSeatAllocate === 1
          ? [
              {
                value: "Seat Allocated",
                label: t?.seatAllocated || "Seat Allocated",
              },
              { value: "Rejected", label: t?.reject || "Rejected" },
            ]
          : [{ value: "Rejected", label: t?.reject || "Rejected" }],
      "Seat Allocated": [],
      "Invite Not Sent": [],
      "Interview Scheduled": [
        { value: "Approved", label: t?.approved || "Approved" },
        { value: "Rejected", label: t?.reject || "Rejected" },
      ],
      Approved: [{ value: "Rejected", label: t?.reject || "Rejected" }],
      Rejected: [{ value: "Proceed", label: t?.proceed || "Proceed" }],
    };

    const options = statusMap[currentStatus] || [];

    return [...baseOptions, ...options];
  };

  const shouldDisableStatus = (currentStatus) => {
    if (!currentStatus || currentStatus === "") {
      return true;
    }

    const disabledStatuses = ["Seat Allocated", "Invite Not Sent", "Admitted"];

    if (disabledStatuses.includes(currentStatus)) {
      return true;
    }

    const validStatuses = [
      "Submitted",
      "Proceed",
      "Interview Scheduled",
      "Approved",
      "Preview",
      "Rejected",
    ];

    return !validStatuses.includes(currentStatus);
  };

  // ==================== FETCH MASTER DATA ====================
  const fetchAllMasterData = useCallback(() => {
    dispatch(fetchReligionOptions());
    dispatch(fetchCategoryOptions());
    dispatch(fetchOccupationOptions());
    dispatch(fetchClassOptions({ id: school_id, academic_year }));
    dispatch(fetchCountryOptions()).then((action) => {
      if (action.payload && Array.isArray(action.payload)) {
        const india = action.payload.find(
          (c) => c.label?.toLowerCase() === "india",
        );
        if (india)
          dispatch(
            fetchStateOptions({
              countryId: india.value,
              stateType: STATE_TYPES.FATHER,
            }),
          );
      }
    });
  }, [dispatch, school_id, academic_year]);

  const fetchSectionList = useCallback(
    async (classId) => {
      if (!classId || !school_id) return;
      try {
        const response = await AxiosInstance.post("/school/class/viewList", {
          id: parseInt(classId),
          school_id: parseInt(school_id),
        });
        if (response?.data?.success) {
          const sections = (response.data.data || []).map((item) => ({
            value: item.section_id,
            label: item.section_id,
          }));
          setSectionOptions(sections);
        } else {
          setSectionOptions([]);
        }
      } catch (error) {
        console.error("Error fetching section list:", error);
        setSectionOptions([]);
      }
    },
    [school_id],
  );

  // Fetch sections when assigned_class_id changes
  useEffect(() => {
    if (formData.assigned_class_id) {
      fetchSectionList(formData.assigned_class_id);
    }
  }, [formData.assigned_class_id, fetchSectionList]);

  // ==================== FETCH ADMISSION DATA ====================
  const fetchAdmissionData = useCallback(async () => {
    if (!admissionId) return;
    try {
      setLoading(true);
      const response = await AxiosInstance.post(
        "/admission/viewSecondryAdmission",
        { id: admissionId },
      );
      if (response.data?.status && response.data?.data) {
        const d = response.data.data;
        const fmt = (val) => (val ? dayjs(val).format("YYYY-MM-DD") : null);

        const formLang = d?.form_language || "en";
        dispatch(switchFormLanguage(formLang));

        rawApiDataRef.current = {
          religion: d.religion || "",
          community: d.community || "",
          student_native_state: d.student_native_state || "",
          gender: d.gender || "",
          parent_occupation: d.parent_occupation || "",
          parent_state: d.parent_state || "",
          guardian_occupation: d.guardian_occupation || "",
          guardian_state: d.guardian_state || "",
          last_studied_class: d.last_studied_class || "",
          preferred_class: d.preferred_class || "",
          mother_tongue: d.mother_tongue || "",
          preferred_first_language: d.preferred_first_language || "",
          previous_studied_class_1: d.previous_studied_class_1 || "",
          previous_studied_class_2: d.previous_studied_class_2 || "",
          previous_studied_class_3: d.previous_studied_class_3 || "",
          previous_studied_year_1:
            d.previous_studied_year_1 != null
              ? String(d.previous_studied_year_1)
              : "",
          previous_studied_year_2:
            d.previous_studied_year_2 != null
              ? String(d.previous_studied_year_2)
              : "",
          previous_studied_year_3:
            d.previous_studied_year_3 != null
              ? String(d.previous_studied_year_3)
              : "",
          living_with: d.living_with || "",
          last_studied_medium: d.last_studied_medium || "",
          qualified_higher_secondary: d.qualified_higher_secondary || "",
          attached_document_type: d.attached_document_type || "",
          preferred_stream: d.preferred_stream || "",
          is_vaccinated_pox: d.is_vaccinated_pox || "",
          previous_studied_medium_1: d.previous_studied_medium_1 || "",
          previous_studied_medium_2: d.previous_studied_medium_2 || "",
          previous_studied_medium_3: d.previous_studied_medium_3 || "",
        };
        normalizedOnceRef.current = false;

        setIsSeatAllocate(d.is_seatAllocate || 0);
        setFormData({
          assigned_class: d.assigned_class || null,
          assigned_class_id: d.assigned_class_id || null,
          assigned_section: d.assigned_section || null,
          date: fmt(d.date) || moment().format("YYYY-MM-DD"),
          student_name: d.student_name || "",
          student_name_in_tamil: d.student_name_in_tamil || "",
          emis_id: d.emis_id || "",
          aadhar_no: d.aadhaar_no || "",
          email: d.email || "",
          mobile_number: d.mobile_number || "",
          student_image: d.student_image || "",
          student_image_original_name:
            d.student_original_path || d.student_image || "",
          date_of_birth: fmt(d.date_of_birth),
          date_of_birth_words: d.date_of_birth_words || "",
          caste: d.caste || "",
          nationality: d.nationality || "Indian",
          parent_name: d.parent_name || "",
          parent_annual_salary:
            d.parent_annual_salary != null
              ? String(d.parent_annual_salary)
              : "",
          parent_address: d.parent_address || "",
          parent_city: d.parent_city || "",
          parent_district: d.parent_district || "",
          parent_pincode:
            d.parent_pincode != null ? String(d.parent_pincode) : "",
          guardian_name: d.guardian_name || "",
          guardian_annual_salary:
            d.guardian_annual_salary != null
              ? String(d.guardian_annual_salary)
              : "",
          guardian_address: d.guardian_address || "",
          guardian_city: d.guardian_city || "",
          guardian_district: d.guardian_district || "",
          guardian_pincode:
            d.guardian_pincode != null ? String(d.guardian_pincode) : "",
          last_studied_school: d.last_studied_school || "",
          document_path: d.document_path || "",
          document_original_name:
            d.document_original_path || d.document_path || "",
          previous_school_address_1: d.previous_school_address_1 || "",
          previous_school_address_2: d.previous_school_address_2 || "",
          previous_school_address_3: d.previous_school_address_3 || "",
          place: d.place || "",
          identification_mark_1: d.identification_mark_1 || "",
          identification_mark_2: d.identification_mark_2 || "",
          current_status: d.status || "",
          ocr_upload_path: d.ocr_upload_path || null,
          status: "",
        });
      }
    } catch (error) {
      console.error("View admission error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details.",
      });
    } finally {
      setLoading(false);
    }
  }, [admissionId, t, dispatch]);

  // ==================== DEFERRED DROPDOWN NORMALIZATION ====================
  useEffect(() => {
    if (!rawApiDataRef.current || normalizedOnceRef.current) return;
    const hasOptions =
      reduxReligionOptions.length > 0 ||
      reduxCategoryOptions.length > 0 ||
      reduxFatherStateOptions.length > 0 ||
      reduxClassOptions.length > 0;
    if (!hasOptions) return;

    normalizedOnceRef.current = true;
    const raw = rawApiDataRef.current;

    setFormData((prev) => {
      const normalizedGender = findMatchInOptions(raw.gender, genderOptions);
      const normalizedReligion = findMatchInOptions(
        raw.religion,
        religionOptions,
      );
      const normalizedCommunity = findMatchInOptions(
        raw.community,
        categoryOptions,
      );
      const normalizedStudentNativeState = findMatchInOptions(
        raw.student_native_state,
        stateOptions,
      );
      const normalizedParentOccupation = findMatchInOptions(
        raw.parent_occupation,
        occupationOptions,
        "label",
      );
      const normalizedParentState = findMatchInOptions(
        raw.parent_state,
        stateOptions,
      );
      const normalizedGuardianOccupation = findMatchInOptions(
        raw.guardian_occupation,
        occupationOptions,
        "label",
      );
      const normalizedGuardianState = findMatchInOptions(
        raw.guardian_state,
        stateOptions,
      );
      const normalizedLastStudiedClass = findMatchInOptions(
        raw.last_studied_class,
        classOptions,
        "label",
      );
      const normalizedPreferredClass = findMatchInOptions(
        raw.preferred_class,
        classOptions,
        "label",
      );
      const normalizedMotherTongue = findMatchInOptions(
        raw.mother_tongue,
        motherTongueOptions,
      );
      const normalizedPreferredFirstLanguage = findMatchInOptions(
        raw.preferred_first_language,
        languageOptions,
      );
      const normalizedPrevClass1 = findMatchInOptions(
        raw.previous_studied_class_1,
        classOptions,
        "label",
      );
      const normalizedPrevClass2 = findMatchInOptions(
        raw.previous_studied_class_2,
        classOptions,
        "label",
      );
      const normalizedPrevClass3 = findMatchInOptions(
        raw.previous_studied_class_3,
        classOptions,
        "label",
      );
      const normalizedPrevYear1 = findMatchInOptions(
        raw.previous_studied_year_1,
        YEAR_OPTIONS,
      );
      const normalizedPrevYear2 = findMatchInOptions(
        raw.previous_studied_year_2,
        YEAR_OPTIONS,
      );
      const normalizedPrevYear3 = findMatchInOptions(
        raw.previous_studied_year_3,
        YEAR_OPTIONS,
      );

      const normalizedLivingWith = normalizeValueAgainstOptions(
        raw.living_with,
        LIVING_WITH_OPTIONS,
      );
      const normalizedLastStudiedMedium = normalizeValueAgainstOptions(
        raw.last_studied_medium,
        MEDIUM_OPTIONS,
      );
      const normalizedQualifiedHS = normalizeValueAgainstOptions(
        raw.qualified_higher_secondary,
        YES_NO_OPTIONS,
      );
      const normalizedDocType = normalizeValueAgainstOptions(
        raw.attached_document_type,
        DOCUMENT_TYPE_OPTIONS,
      );
      const normalizedPreferredStream = normalizeValueAgainstOptions(
        raw.preferred_stream,
        MEDIUM_OPTIONS,
      );
      const normalizedVaccinated = normalizeValueAgainstOptions(
        raw.is_vaccinated_pox,
        YES_NO_OPTIONS,
      );
      const normalizedPrevMedium1 = normalizeValueAgainstOptions(
        raw.previous_studied_medium_1,
        MEDIUM_OPTIONS,
      );
      const normalizedPrevMedium2 = normalizeValueAgainstOptions(
        raw.previous_studied_medium_2,
        MEDIUM_OPTIONS,
      );
      const normalizedPrevMedium3 = normalizeValueAgainstOptions(
        raw.previous_studied_medium_3,
        MEDIUM_OPTIONS,
      );

      return {
        ...prev,
        gender: normalizedGender,
        religion: normalizedReligion,
        community: normalizedCommunity,
        student_native_state: normalizedStudentNativeState,
        parent_occupation: normalizedParentOccupation,
        parent_state: normalizedParentState,
        guardian_occupation: normalizedGuardianOccupation,
        guardian_state: normalizedGuardianState,
        last_studied_class: normalizedLastStudiedClass,
        preferred_class: normalizedPreferredClass,
        mother_tongue: normalizedMotherTongue,
        preferred_first_language: normalizedPreferredFirstLanguage,
        previous_studied_class_1: normalizedPrevClass1,
        previous_studied_class_2: normalizedPrevClass2,
        previous_studied_class_3: normalizedPrevClass3,
        previous_studied_year_1: normalizedPrevYear1,
        previous_studied_year_2: normalizedPrevYear2,
        previous_studied_year_3: normalizedPrevYear3,
        living_with: normalizedLivingWith,
        last_studied_medium: normalizedLastStudiedMedium || "TM",
        qualified_higher_secondary: normalizedQualifiedHS || "No",
        attached_document_type: normalizedDocType || "TC",
        preferred_stream: normalizedPreferredStream || "TM",
        is_vaccinated_pox: normalizedVaccinated || "Yes",
        previous_studied_medium_1: normalizedPrevMedium1 || "TM",
        previous_studied_medium_2: normalizedPrevMedium2 || "TM",
        previous_studied_medium_3: normalizedPrevMedium3 || "TM",
      };
    });
  }, [
    reduxReligionOptions,
    reduxCategoryOptions,
    reduxFatherStateOptions,
    reduxOccupationOptions,
    reduxClassOptions,
    genderOptions,
    religionOptions,
    categoryOptions,
    stateOptions,
    occupationOptions,
    classOptions,
    motherTongueOptions,
    languageOptions,
  ]);

  // ==================== ON OPEN ====================
  useEffect(() => {
    if (open && admissionId) {
      fetchAllMasterData();
      fetchAdmissionData();
    }
    if (!open) {
      setFormData({ ...INITIAL_FORM_DATA });
      setErrors({});
      setShowTamilKeyboard(false);
      rawApiDataRef.current = null;
      normalizedOnceRef.current = false;
      dispatch(resetFormLanguage());
    }
  }, [open, admissionId, fetchAllMasterData, fetchAdmissionData]);

  // ==================== HANDLERS ====================
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (
        NUMBER_ONLY_FIELDS.includes(name) &&
        value !== "" &&
        !/^\d*$/.test(value)
      )
        return;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleDropdownChange = useCallback(
    (name, value) => {
      if (name === "assigned_class_id") {
        const selectedOption = assignedClassOptions.find(
          (opt) => opt.value === (value?.value ?? value),
        );
        setFormData((prev) => ({
          ...prev,
          assigned_class_id: value?.value ?? value,
          assigned_class: selectedOption?.label || null,
          assigned_section: null,
        }));
        setSectionOptions([]);
        if (errors.assigned_class_id)
          setErrors((prev) => ({ ...prev, assigned_class_id: "" }));
        return;
      }

      if (name === "status") {
        const newStatusVal = value?.value ?? value;
        setFormData((prev) => ({
          ...prev,
          status: value,
          // Clear feedback if the new status is not Rejected/Approved
          feedback:
            newStatusVal === "Rejected" || newStatusVal === "Approved"
              ? prev.feedback
              : "",
        }));
        if (errors.status) setErrors((prev) => ({ ...prev, status: "" }));
        if (errors.feedback) setErrors((prev) => ({ ...prev, feedback: "" }));
        return;
      }

      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors, assignedClassOptions],
  );

  const handleRadioChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleDateChange = useCallback(
    (name, date) => {
      const formatted = date ? dayjs(date).format("YYYY-MM-DD") : null;
      setFormData((prev) => ({
        ...prev,
        [name]: formatted,
        ...(name === "date_of_birth"
          ? { date_of_birth_words: convertDateToWords(formatted) }
          : {}),
      }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  // Tamil keyboard
  const handleTamilInputFocus = (name) => {
    if (!NUMBER_ONLY_FIELDS.includes(name)) setActiveInputName(name);
  };

  const handleTamilKeyPress = (char) => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "") + char,
    }));
    if (errors[activeInputName])
      setErrors((prev) => ({ ...prev, [activeInputName]: "" }));
  };

  const handleTamilBackspace = () => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "").slice(0, -1),
    }));
  };

  const handleTamilSpace = () => {
    if (!activeInputName) return;
    setFormData((prev) => ({
      ...prev,
      [activeInputName]: (prev[activeInputName] || "") + " ",
    }));
  };

  // File uploads
  const handleFileUpload = useCallback(
    async (file, fieldKey, uploadKey, allowedTypes) => {
      if (!file) {
        setFormData((prev) => ({
          ...prev,
          [`${fieldKey}_original_name`]: "",
          [fieldKey]: "",
        }));
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [fieldKey]: t?.allowedPdfJpgPng || "Invalid file type",
        }));
        return;
      }
      try {
        const payload = new FormData();
        payload.append(uploadKey, file);
        if (school_id) {
          payload.append("school_id", school_id);
        }
        const response = await AxiosInstance.post(
          "/groupCockpit/upload",
          payload,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
          const uploaded = response.data[0];
          if (fieldKey === "document_path") {
            setFormData((prev) => ({
              ...prev,
              document_original_name: uploaded.original_name,
              [fieldKey]: uploaded.upload_path,
            }));
            setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
          } else {
            setFormData((prev) => ({
              ...prev,
              student_image_original_name: uploaded.original_name,
              [fieldKey]: uploaded.upload_path,
            }));
            setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
          }
        }
      } catch (error) {
        console.error(`${fieldKey} upload error:`, error);
        setErrors((prev) => ({
          ...prev,
          [fieldKey]: t?.fileUploadFailed || "Upload failed.",
        }));
      }
    },
    [t],
  );

  const handleDocumentChange = useCallback(
    (file) => {
      handleFileUpload(file, "document_path", "transfer_certificate", [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ]);
    },
    [handleFileUpload],
  );

  const handleStudentImageChange = useCallback(
    (file) => {
      handleFileUpload(file, "student_image", "admission_student_image", [
        "image/jpeg",
        "image/jpg",
        "image/png",
      ]);
    },
    [handleFileUpload],
  );

  // Helpers
  const getVal = (field) => {
    if (!field && field !== 0) return null;
    return typeof field === "object" && field.value !== undefined
      ? field.value
      : field;
  };

  const getLabel = (field) => {
    if (!field && field !== 0) return null;
    return typeof field === "object" && field.label !== undefined
      ? field.label
      : field;
  };

  const isGuardian = getVal(formData.living_with) === "guardian";

  // ==================== UPDATE API ====================
  const handleUpdateChanges = async () => {
    // Pass isSeatAllocate to validation
    const validationErrors = validateSecondaryEditForm(
      formData,
      translations,
      isSeatAllocate,
    );
    console.log("validationErrors", validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorKey = Object.keys(validationErrors)[0];
      const el = document.querySelector(`[name="${firstErrorKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const livingWith = getVal(formData.living_with);
    const isParent = livingWith === "parent";
    const isGuard = livingWith === "guardian";

    try {
      setSubmitting(true);
      const payload = {
        assigned_section: formData.assigned_section
          ? typeof formData.assigned_section === "object"
            ? formData.assigned_section.value
            : formData.assigned_section
          : null,
        assigned_class: formData.assigned_class || null,
        assigned_class_id: formData.assigned_class_id || null,
        id: admissionId,
        student_name: getVal(formData.student_name),
        student_name_in_tamil: getVal(formData.student_name_in_tamil),
        emis_id: getVal(formData.emis_id) || null,
        email: getVal(formData.email),
        aadhaar_no: formData.aadhar_no,
        mobile_number: getVal(formData.mobile_number),
        student_image: getVal(formData.student_image) || null,
        student_original_path: formData.student_image_original_name || null,
        gender: getLabel(formData.gender),
        date_of_birth: getVal(formData.date_of_birth),
        date_of_birth_words: getVal(formData.date_of_birth_words),
        religion: getLabel(formData.religion),
        caste: getVal(formData.caste),
        community: getLabel(formData.community),
        nationality: getVal(formData.nationality),
        student_native_state: getLabel(formData.student_native_state),
        living_with: livingWith,
        parent_name: isParent ? getVal(formData.parent_name) : null,
        parent_occupation: isParent
          ? getLabel(formData.parent_occupation)
          : null,
        parent_annual_salary: isParent
          ? parseInt(formData.parent_annual_salary) || 0
          : null,
        parent_address: isParent ? getVal(formData.parent_address) : null,
        parent_city: isParent ? getVal(formData.parent_city) : null,
        parent_district: isParent ? getVal(formData.parent_district) : null,
        parent_state: isParent ? getLabel(formData.parent_state) : null,
        parent_pincode: isParent ? getVal(formData.parent_pincode) : null,
        guardian_name: isGuard ? getVal(formData.guardian_name) : null,
        guardian_occupation: isGuard
          ? getLabel(formData.guardian_occupation)
          : null,
        guardian_annual_salary: isGuard
          ? parseInt(formData.guardian_annual_salary) || 0
          : null,
        guardian_address: isGuard ? getVal(formData.guardian_address) : null,
        guardian_city: isGuard ? getVal(formData.guardian_city) : null,
        guardian_district: isGuard ? getVal(formData.guardian_district) : null,
        guardian_state: isGuard ? getLabel(formData.guardian_state) : null,
        guardian_pincode: isGuard ? getVal(formData.guardian_pincode) : null,
        last_studied_medium: getVal(formData.last_studied_medium),
        last_studied_class: getLabel(formData.last_studied_class),
        last_studied_school: getVal(formData.last_studied_school),
        qualified_higher_secondary: getVal(formData.qualified_higher_secondary),
        attached_document_type: getVal(formData.attached_document_type),
        document_path: getVal(formData.document_path) || null,
        document_original_path: formData.document_original_name || null,
        preferred_class: getLabel(formData.preferred_class),
        mother_tongue: getVal(formData.mother_tongue),
        preferred_first_language: getVal(formData.preferred_first_language),
        preferred_stream: getVal(formData.preferred_stream),
        is_vaccinated_pox: getVal(formData.is_vaccinated_pox),
        previous_school_address_1:
          getVal(formData.previous_school_address_1) || null,
        previous_studied_class_1:
          getLabel(formData.previous_studied_class_1) || null,
        previous_studied_medium_1:
          getVal(formData.previous_studied_medium_1) || null,
        previous_studied_year_1:
          getVal(formData.previous_studied_year_1) || null,
        previous_school_address_2:
          getVal(formData.previous_school_address_2) || null,
        previous_studied_class_2:
          getLabel(formData.previous_studied_class_2) || null,
        previous_studied_medium_2:
          getVal(formData.previous_studied_medium_2) || null,
        previous_studied_year_2:
          getVal(formData.previous_studied_year_2) || null,
        previous_school_address_3:
          getVal(formData.previous_school_address_3) || null,
        previous_studied_class_3:
          getLabel(formData.previous_studied_class_3) || null,
        previous_studied_medium_3:
          getVal(formData.previous_studied_medium_3) || null,
        previous_studied_year_3:
          getVal(formData.previous_studied_year_3) || null,
        place: getVal(formData.place),
        identification_mark_1: getVal(formData.identification_mark_1),
        identification_mark_2: getVal(formData.identification_mark_2),
        status: formData.status?.value
          ? formData.status.value
          : formData.current_status,
        updated_by: user_id || 1,
        feedback: getVal(formData.feedback) || null,
        form_language: formLanguage || language,
      };

      const response = await AxiosInstance.put(
        "/admission/updateSecondryAdmission",
        payload,
      );

      if (response.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: t?.updateSuccess || "Updated successfully!",
        });
        setTimeout(() => {
          setModalState({ open: false, type: "", message: "" });
          onClose(true);
          fetchAdmissionList();
        }, 1500);
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message || t?.updateFailed || "Update failed.",
        });
      }
    } catch (error) {
      console.error("Update error:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error.response?.data?.message || t?.updateFailed || "Update failed.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ ...INITIAL_FORM_DATA });
    setErrors({});
    setShowTamilKeyboard(false);
    rawApiDataRef.current = null;
    normalizedOnceRef.current = false;
    dispatch(resetFormLanguage());
    onClose(false);
  };

  // ==================== SECTION HEADER COMPONENT ====================
  const SectionHeader = ({ title }) => (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionIcon}>
        <FaClipboardList />
      </div>
      <h2 className={styles.sectionTitle}>{title}</h2>
    </div>
  );

  // OCR PDF preview — blob paths (Upload/OfflineApplications/...) are
  // fetched through /content/image into an object URL; legacy paths
  // keep the static URL.
  const [ocrPreviewUrl, setOcrPreviewUrl] = useState("");
  useEffect(() => {
    const p = formData.ocr_upload_path || "";
    if (!p) {
      setOcrPreviewUrl("");
      return undefined;
    }
    const isBlobPath =
      !/^https?:\/\//i.test(p) && !p.startsWith("/") && p.includes("/Upload/");
    if (!isBlobPath) {
      setOcrPreviewUrl(/^https?:\/\//i.test(p) ? p : `${IMAGE_BASE_URL}${p}`);
      return undefined;
    }
    let cancelled = false;
    AxiosInstance.get("/content/image", {
      params: { path: p },
      responseType: "blob",
    })
      .then((res) => {
        if (cancelled) return;
        setOcrPreviewUrl(
          URL.createObjectURL(
            new Blob([res.data], {
              type: res.headers["content-type"] || "application/pdf",
            }),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setOcrPreviewUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [formData.ocr_upload_path]);

  // Blob-aware preview: raw stored path in — blob paths fetched through
  // the authenticated /content/image endpoint into an object URL,
  // legacy /uploads paths keep the old static URL.
  const handlePreview = async (path) => {
    if (!path) return;
    const p = String(path);
    const isBlobPath =
      !/^https?:\/\//i.test(p) && !p.startsWith("/") && p.includes("/Upload/");
    if (isBlobPath) {
      try {
        const res = await AxiosInstance.get("/content/image", {
          params: { path: p },
          responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(
          new Blob([res.data], {
            type: res.headers["content-type"] || "application/octet-stream",
          }),
        );
        window.open(objectUrl, "_blank");
      } catch (err) {
        console.error("Preview error:", err);
      }
      return;
    }
    window.open(/^https?:\/\//i.test(p) ? p : `${IMAGE_BASE_URL}${p}`, "_blank");
  };

  // ==================== RENDER ====================
  return (
    <>
      <Modal
        show={open}
        onHide={handleClose}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
        scrollable
        className={styles.modalDialog}
      >
        {/* HEADER */}
        <Modal.Header closeButton className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <Modal.Title className={styles.modalTitle}>
              {t?.secondaryOnlineApplication || "Secondary Online Application"}{" "}
              {"-"} {realAdmissionId || ""}
            </Modal.Title>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button
                type="button"
                className={`${styles.tamilKeyboardToggle} ${showTamilKeyboard ? styles.tamilKeyboardToggleActive : ""}`}
                onClick={() => setShowTamilKeyboard((prev) => !prev)}
              >
                {t?.tamilKeyboard || "தமிழ் விசைப்பலகை"}
              </button>
            </div>
          </div>
        </Modal.Header>

        {/* BODY */}
        <Modal.Body className={styles.modalBody}>
          {loading ? (
            <div className={styles.loadingContainer}>
              {t?.loading || "Loading..."}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                gap: formData.ocr_upload_path ? "20px" : "0",
              }}
            >
              {formData.ocr_upload_path && (
                <div className={styles.previewPane}>
                  <iframe
                    src={ocrPreviewUrl}
                    title="PDF Preview"
                    width="100%"
                    height="100%"
                    style={{ border: "none" }}
                  />
                </div>
              )}

              <div
                className={styles.formPane}
                style={{
                  width: formData.ocr_upload_path ? undefined : "100%",
                }}
              >
                <div className={styles.formContainer}>
                  {/* TAMIL KEYBOARD */}
                  {showTamilKeyboard && (
                    <div
                      ref={keyboardRef}
                      className={styles.tamilKeyboard}
                      style={{ top: kbPosition.y, left: kbPosition.x }}
                    >
                      <div
                        className={styles.tamilKeyboardHeader}
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                        style={{ cursor: isDragging ? "grabbing" : "grab" }}
                      >
                        <div className={styles.tamilKeyboardHeaderLeft}>
                          <FaGripHorizontal
                            size={14}
                            style={{ opacity: 0.6 }}
                          />
                          <h5 className={styles.tamilKeyboardTitle}>
                            {t?.tamilKeyboard || "தமிழ் விசைப்பலகை"}
                          </h5>
                        </div>
                        <button
                          type="button"
                          className={styles.tamilKeyboardClose}
                          onClick={() => setShowTamilKeyboard(false)}
                        >
                          <FaTimes />
                        </button>
                      </div>
                      <div className={styles.tamilKeyboardBody}>
                        <div className={styles.tamilKeyRow}>
                          {TAMIL_VOWELS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              className={styles.tamilKey}
                              onClick={() => handleTamilKeyPress(c)}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                        <div className={styles.tamilKeyRow}>
                          {TAMIL_CONSONANTS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              className={styles.tamilKey}
                              onClick={() => handleTamilKeyPress(c)}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                        <div className={styles.tamilKeyRow}>
                          {TAMIL_MODIFIERS.map((c, i) => (
                            <button
                              key={i}
                              type="button"
                              className={styles.tamilKey}
                              onClick={() => handleTamilKeyPress(c)}
                            >
                              {c}
                            </button>
                          ))}
                          <button
                            type="button"
                            className={styles.tamilKeyBackspace}
                            onClick={handleTamilBackspace}
                          >
                            ⌫
                          </button>
                          <button
                            type="button"
                            className={styles.tamilKeySpace}
                            onClick={handleTamilSpace}
                          >
                            ␣
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ===== BASIC DETAILS ===== */}
                  <SectionHeader title={t?.basicDetails || "Basic Details"} />
                  <div className="row">
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.date || "Date"}
                        name="date"
                        value={formData.date}
                        disabled
                        readOnly
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={
                          t?.studentNameEnglish || "Student Name (English)"
                        }
                        name="student_name"
                        value={formData.student_name}
                        onChange={handleInputChange}
                        onFocus={() => handleTamilInputFocus("student_name")}
                        placeholder={
                          t?.studentNameEnglishPlaceholder ||
                          "Enter Student Name in English"
                        }
                        required
                        error={errors.student_name}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.studentNameTamil || "Student Name (Tamil)"}
                        name="student_name_in_tamil"
                        value={formData.student_name_in_tamil}
                        onChange={handleInputChange}
                        onFocus={() =>
                          handleTamilInputFocus("student_name_in_tamil")
                        }
                        placeholder={
                          t?.studentNameTamilPlaceholder ||
                          "Enter Student Name in Tamil"
                        }
                        required
                        error={errors.student_name_in_tamil}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.aadharNumber || "Aadhaar No."}
                        name="aadhar_no"
                        value={formData.aadhar_no}
                        onChange={handleInputChange}
                        placeholder={
                          t?.aadharNumberAltPlaceholder || "Ex: 432156789076"
                        }
                        maxLength={12}
                        error={errors.aadhar_no}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDatePicker
                        label={t?.dateOfBirth || "Date of Birth"}
                        name="date_of_birth"
                        value={
                          formData.date_of_birth
                            ? dayjs(formData.date_of_birth)
                            : null
                        }
                        onChange={(date) =>
                          handleDateChange("date_of_birth", date)
                        }
                        placeholder={
                          t?.dateOfBirthPlaceholder || "Ex: 30/07/2016"
                        }
                        required
                        error={errors.date_of_birth}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.emailAddress || "Email"}
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={
                          t?.emailPlaceholder || "E.g. student@gmail.com"
                        }
                        required
                        error={errors.email}
                        disabled={formData.current_status === "Admitted"}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.mobileNumber || "Mobile No."}
                        name="mobile_number"
                        value={formData.mobile_number}
                        onChange={handleInputChange}
                        placeholder={
                          t?.mobileNumberPlaceholder || "E.g. Mobile Number"
                        }
                        maxLength={10}
                        required
                        error={errors.mobile_number}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.gender || "Gender"}
                        name="gender"
                        value={formData.gender}
                        onChange={(v) => handleDropdownChange("gender", v)}
                        options={genderOptions}
                        placeholder={t?.selectGender || "Select Gender"}
                        required
                        error={errors.gender}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.religion || "Religion"}
                        name="religion"
                        value={formData.religion}
                        onChange={(v) => handleDropdownChange("religion", v)}
                        options={religionOptions}
                        placeholder={t?.selectReligion || "Select Religion"}
                        required
                        error={errors.religion}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.caste || "Caste"}
                        name="caste"
                        value={formData.caste}
                        onChange={handleInputChange}
                        onFocus={() => handleTamilInputFocus("caste")}
                        placeholder={t?.castePlaceholder || "E.g. Caste"}
                        required
                        error={errors.caste}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.category || "Category"}
                        name="community"
                        value={formData.community}
                        onChange={(v) => handleDropdownChange("community", v)}
                        options={categoryOptions}
                        placeholder={t?.selectCategory || "Select Category"}
                        required
                        error={errors.community}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.nationality || "Nationality"}
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleInputChange}
                        placeholder={t?.nationalityPlaceholder || "E.g. Indian"}
                        required
                        error={errors.nationality}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.state || "State"}
                        name="student_native_state"
                        value={formData.student_native_state}
                        onChange={(v) =>
                          handleDropdownChange("student_native_state", v)
                        }
                        options={stateOptions}
                        placeholder={t?.selectState || "Select State"}
                        required
                        error={errors.student_native_state}
                      />
                    </div>
                  </div>

                  {/* ===== RESIDENTIAL DETAILS ===== */}
                  <SectionHeader
                    title={t?.residentialDetails || "Residential Details"}
                  />
                  <div className="row">
                    <div className="col-12 px-2 mb-3">
                      <CustomDropdown
                        label={
                          t?.areYouResidingWith ||
                          "Residing with parents or guardian?"
                        }
                        name="living_with"
                        value={formData.living_with}
                        onChange={(v) => handleDropdownChange("living_with", v)}
                        options={livingWithOptions}
                        placeholder={t?.select || "Select"}
                        required
                        error={errors.living_with}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.fullname || "Name"}
                        name={isGuardian ? "guardian_name" : "parent_name"}
                        value={
                          isGuardian
                            ? formData.guardian_name
                            : formData.parent_name
                        }
                        onChange={handleInputChange}
                        onFocus={() =>
                          handleTamilInputFocus(
                            isGuardian ? "guardian_name" : "parent_name",
                          )
                        }
                        placeholder={t?.enterHere || "Enter here.."}
                        required
                        error={
                          isGuardian ? errors.guardian_name : errors.parent_name
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.occupation || "Occupation"}
                        name={
                          isGuardian
                            ? "guardian_occupation"
                            : "parent_occupation"
                        }
                        value={
                          isGuardian
                            ? formData.guardian_occupation
                            : formData.parent_occupation
                        }
                        onChange={(v) =>
                          handleDropdownChange(
                            isGuardian
                              ? "guardian_occupation"
                              : "parent_occupation",
                            v,
                          )
                        }
                        options={occupationOptions}
                        placeholder={t?.selectOccupation || "Select Occupation"}
                        required
                        error={
                          isGuardian
                            ? errors.guardian_occupation
                            : errors.parent_occupation
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.annualIncome || "Annual Income"}
                        name={
                          isGuardian
                            ? "guardian_annual_salary"
                            : "parent_annual_salary"
                        }
                        value={
                          isGuardian
                            ? formData.guardian_annual_salary
                            : formData.parent_annual_salary
                        }
                        onChange={handleInputChange}
                        placeholder={
                          t?.annualIncomePlaceholder || "E.g. 500000"
                        }
                        maxLength={7}
                        required
                        error={
                          isGuardian
                            ? errors.guardian_annual_salary
                            : errors.parent_annual_salary
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.address || "Address"}
                        name={
                          isGuardian ? "guardian_address" : "parent_address"
                        }
                        value={
                          isGuardian
                            ? formData.guardian_address
                            : formData.parent_address
                        }
                        onChange={handleInputChange}
                        onFocus={() =>
                          handleTamilInputFocus(
                            isGuardian ? "guardian_address" : "parent_address",
                          )
                        }
                        placeholder={t?.addressPlaceholder || "E.g. Address"}
                        required
                        error={
                          isGuardian
                            ? errors.guardian_address
                            : errors.parent_address
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.city || "City"}
                        name={isGuardian ? "guardian_city" : "parent_city"}
                        value={
                          isGuardian
                            ? formData.guardian_city
                            : formData.parent_city
                        }
                        onChange={handleInputChange}
                        onFocus={() =>
                          handleTamilInputFocus(
                            isGuardian ? "guardian_city" : "parent_city",
                          )
                        }
                        placeholder={t?.cityPlaceholder || "E.g. City"}
                        required
                        error={
                          isGuardian ? errors.guardian_city : errors.parent_city
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.district || "District"}
                        name={
                          isGuardian ? "guardian_district" : "parent_district"
                        }
                        value={
                          isGuardian
                            ? formData.guardian_district
                            : formData.parent_district
                        }
                        onChange={handleInputChange}
                        onFocus={() =>
                          handleTamilInputFocus(
                            isGuardian
                              ? "guardian_district"
                              : "parent_district",
                          )
                        }
                        placeholder={t?.districtPlaceholder || "E.g. District"}
                        required
                        error={
                          isGuardian
                            ? errors.guardian_district
                            : errors.parent_district
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.state || "State"}
                        name={isGuardian ? "guardian_state" : "parent_state"}
                        value={
                          isGuardian
                            ? formData.guardian_state
                            : formData.parent_state
                        }
                        onChange={(v) =>
                          handleDropdownChange(
                            isGuardian ? "guardian_state" : "parent_state",
                            v,
                          )
                        }
                        options={stateOptions}
                        placeholder={t?.selectState || "Select State"}
                        required
                        error={
                          isGuardian
                            ? errors.guardian_state
                            : errors.parent_state
                        }
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.pincode || "Pincode"}
                        name={
                          isGuardian ? "guardian_pincode" : "parent_pincode"
                        }
                        value={
                          isGuardian
                            ? formData.guardian_pincode
                            : formData.parent_pincode
                        }
                        onChange={handleInputChange}
                        placeholder={t?.pincodePlaceholder || "6-digit pincode"}
                        maxLength={6}
                        required
                        error={
                          isGuardian
                            ? errors.guardian_pincode
                            : errors.parent_pincode
                        }
                      />
                    </div>
                  </div>

                  {/* ===== LAST STUDIED DETAILS ===== */}
                  <SectionHeader
                    title={t?.lastStudiedDetails || "Last Studied Details"}
                  />
                  <div className="row">
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.lastStudiedClass || "Last Studied Class"}
                        name="last_studied_class"
                        value={formData.last_studied_class}
                        onChange={(v) =>
                          handleDropdownChange("last_studied_class", v)
                        }
                        options={classOptions}
                        placeholder={t?.selectClass || "Select"}
                        required
                        error={errors.last_studied_class}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.emisNo || "EMIS No"}
                        name="emis_id"
                        value={formData.emis_id}
                        onChange={handleInputChange}
                        placeholder={t?.emisNoPlaceholder || "E.g. 1212121212"}
                        maxLength={10}
                        required
                        error={errors.emis_id}
                        disabled={formData.current_status === "Admitted"}
                      />
                    </div>
                    <div className="col-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.lastStudiedSchool || "Last Studied School"}
                        name="last_studied_school"
                        value={formData.last_studied_school}
                        onChange={handleInputChange}
                        onFocus={() =>
                          handleTamilInputFocus("last_studied_school")
                        }
                        placeholder={t?.enterHere || "Enter here.."}
                        required
                        error={errors.last_studied_school}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomRadioGroup
                        label={t?.lastStudiedMedium || "Last Studied Medium"}
                        name="last_studied_medium"
                        value={formData.last_studied_medium}
                        onChange={(v) =>
                          handleRadioChange("last_studied_medium", v)
                        }
                        options={MEDIUM_OPTIONS}
                        required
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomRadioGroup
                        label={
                          t?.eligibleForHigherStudies ||
                          "Eligible for higher class?"
                        }
                        name="qualified_higher_secondary"
                        value={formData.qualified_higher_secondary}
                        onChange={(v) =>
                          handleRadioChange("qualified_higher_secondary", v)
                        }
                        options={YES_NO_OPTIONS}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomRadioGroup
                        label={t?.attachedDocumentType || "Document Type"}
                        name="attached_document_type"
                        value={formData.attached_document_type}
                        onChange={(v) =>
                          handleRadioChange("attached_document_type", v)
                        }
                        options={DOCUMENT_TYPE_OPTIONS}
                        required
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomFileInput
                        label={t?.document || "Document"}
                        name="document_path"
                        value={formData.document_original_name || null}
                        onChange={handleDocumentChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        placeholder={
                          formData.document_original_name ||
                          t?.noFileChosen ||
                          "No file chosen"
                        }
                        buttonText={t?.chooseFile || "Choose File"}
                        required
                        error={errors.document_path}
                      />
                      {formData?.document_path && (
                        <button
                          type="button"
                          className={styles.viewBtn}
                          onClick={() =>
                            handlePreview(formData.document_path)
                          }
                        >
                          <FaEye />
                        </button>
                      )}
                      <small className={styles.fileHint}>
                        {t?.allowedPdfJpgPng || "Allowed: PDF/JPG/PNG"}
                      </small>
                    </div>
                  </div>

                  {/* ===== PREFERRED COURSE DETAILS ===== */}
                  <SectionHeader
                    title={
                      t?.preferredCourseDetails || "Preferred Course Details"
                    }
                  />
                  <div className="row">
                    <div className="col-12 px-2 mb-3">
                      <CustomDropdown
                        label={
                          t?.preferredClassForAdmission ||
                          "Preferred Class for Admission"
                        }
                        name="preferred_class"
                        value={formData.preferred_class}
                        onChange={(v) =>
                          handleDropdownChange("preferred_class", v)
                        }
                        options={classOptions}
                        placeholder={
                          t?.selectPreferredClass || "Select Preferred Class"
                        }
                        required
                        error={errors.preferred_class}
                        disabled={formData.current_status === "Admitted"}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.motherTongue || "Mother Tongue"}
                        name="mother_tongue"
                        value={formData.mother_tongue}
                        onChange={(v) =>
                          handleDropdownChange("mother_tongue", v)
                        }
                        options={motherTongueOptions}
                        placeholder={
                          t?.selectMotherTongue || "Select Mother Tongue"
                        }
                        required
                        error={errors.mother_tongue}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={
                          t?.preferredFirstLanguageToStudy ||
                          "Preferred First Language"
                        }
                        name="preferred_first_language"
                        value={formData.preferred_first_language}
                        onChange={(v) =>
                          handleDropdownChange("preferred_first_language", v)
                        }
                        options={languageOptions}
                        placeholder={t?.select || "Select"}
                        required
                        error={errors.preferred_first_language}
                        disabled={formData.current_status === "Admitted"}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomRadioGroup
                        label={t?.preferredMediumToStudy || "Preferred Medium"}
                        name="preferred_stream"
                        value={formData.preferred_stream}
                        onChange={(v) =>
                          handleRadioChange("preferred_stream", v)
                        }
                        options={MEDIUM_OPTIONS}
                        required
                        disabled={formData.current_status === "Admitted"}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomRadioGroup
                        label={
                          t?.smallpoxVaccinationTaken ||
                          "Smallpox Vaccination Taken?"
                        }
                        name="is_vaccinated_pox"
                        value={formData.is_vaccinated_pox}
                        onChange={(v) =>
                          handleRadioChange("is_vaccinated_pox", v)
                        }
                        options={YES_NO_OPTIONS}
                      />
                    </div>
                  </div>

                  {/* ===== PREVIOUS SCHOOL ADDRESS ===== */}
                  <SectionHeader
                    title={
                      t?.previousSchoolAddress || "Previous School Address"
                    }
                  />
                  {[1, 2, 3].map((num) => (
                    <div className="row" key={num}>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-6 col-md-6 col-sm-12 px-2" : "col-lg-3 col-md-6 col-sm-12 px-2 mb-3"}`}
                      >
                        <CustomTextInput
                          label={`${t?.schoolName || "School Name"} ${num}`}
                          name={`previous_school_address_${num}`}
                          value={formData[`previous_school_address_${num}`]}
                          onChange={handleInputChange}
                          onFocus={() =>
                            handleTamilInputFocus(
                              `previous_school_address_${num}`,
                            )
                          }
                          placeholder={t?.enterHere || "Enter here.."}
                          required={num === 1}
                          error={errors[`previous_school_address_${num}`]}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-6 col-md-6 col-sm-12 px-2" : "col-lg-3 col-md-6 col-sm-12 px-2 mb-3"}`}
                      >
                        <CustomDropdown
                          label={t?.classes || "Classes"}
                          name={`previous_studied_class_${num}`}
                          value={formData[`previous_studied_class_${num}`]}
                          onChange={(v) =>
                            handleDropdownChange(
                              `previous_studied_class_${num}`,
                              v,
                            )
                          }
                          options={classOptions}
                          placeholder={t?.selectClass || "Select Class"}
                          required={num === 1}
                          error={errors[`previous_studied_class_${num}`]}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-6 col-md-6 col-sm-12 px-2" : "col-lg-3 col-md-6 col-sm-12 px-2 mb-3"}`}
                      >
                        <CustomDropdown
                          label={t?.yearOfStudy || "Year of Study"}
                          name={`previous_studied_year_${num}`}
                          value={formData[`previous_studied_year_${num}`]}
                          onChange={(v) =>
                            handleDropdownChange(
                              `previous_studied_year_${num}`,
                              v,
                            )
                          }
                          options={YEAR_OPTIONS}
                          placeholder={t?.selectYear || "Select Year"}
                          required={num === 1}
                          error={errors[`previous_studied_year_${num}`]}
                        />
                      </div>
                      <div
                        className={`${formData.ocr_upload_path ? "col-lg-6 col-md-6 col-sm-12 px-2" : "col-lg-3 col-md-6 col-sm-12 px-2 mb-3"}`}
                      >
                        <CustomRadioGroup
                          label={t?.mediumOfInstruction || "Medium"}
                          name={`previous_studied_medium_${num}`}
                          value={formData[`previous_studied_medium_${num}`]}
                          onChange={(v) =>
                            handleRadioChange(
                              `previous_studied_medium_${num}`,
                              v,
                            )
                          }
                          options={MEDIUM_OPTIONS}
                        />
                      </div>
                    </div>
                  ))}

                  {/* ===== IDENTIFICATION MARKS ===== */}
                  <SectionHeader
                    title={
                      t?.identificationMarksOnBody ||
                      "Identification Marks on the Body"
                    }
                  />
                  <div className="row">
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={
                          t?.identificationMark1 || "Identification Mark 1"
                        }
                        name="identification_mark_1"
                        value={formData.identification_mark_1}
                        onChange={handleInputChange}
                        onFocus={() =>
                          handleTamilInputFocus("identification_mark_1")
                        }
                        placeholder={t?.enterHere || "Enter here.."}
                        required
                        error={errors.identification_mark_1}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={
                          t?.identificationMark2 || "Identification Mark 2"
                        }
                        name="identification_mark_2"
                        value={formData.identification_mark_2}
                        onChange={handleInputChange}
                        onFocus={() =>
                          handleTamilInputFocus("identification_mark_2")
                        }
                        placeholder={t?.enterHere || "Enter here.."}
                        required
                        error={errors.identification_mark_2}
                      />
                    </div>
                  </div>

                  {/* ===== OTHER DETAILS ===== */}
                  <SectionHeader title={t?.otherDetails || "Other Details"} />
                  <div className="row">
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.place || "Place"}
                        name="place"
                        value={formData.place}
                        onChange={handleInputChange}
                        onFocus={() => handleTamilInputFocus("place")}
                        placeholder={t?.enterHere || "Enter here.."}
                        required
                        error={errors.place}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomFileInput
                        label={t?.studentImage || "Student Image"}
                        name="student_image"
                        value={formData.student_image_original_name || null}
                        onChange={handleStudentImageChange}
                        accept="image/*"
                        placeholder={
                          formData.student_image_original_name ||
                          t?.noFileChosen ||
                          "No file chosen"
                        }
                        buttonText={t?.chooseFile || "Choose File"}
                        required
                        error={errors.student_image}
                      />
                      {formData?.student_image && (
                        <button
                          type="button"
                          className={styles.viewBtn}
                          onClick={() =>
                            handlePreview(formData.student_image)
                          }
                        >
                          <FaEye />
                        </button>
                      )}
                      <small className={styles.fileHint}>
                        {t?.allowedJpgPng || "Allowed: JPG/PNG"}
                      </small>
                    </div>
                  </div>

                  {/* ===== STATUS ===== */}
                  <SectionHeader title={t?.status || "Status"} />
                  <div className="row">
                    {/* ── Seat Allocation: Assigned Class & Section dropdowns ── */}
                    {showSeatAllocationFields && (
                      <>
                        <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                          <CustomDropdown
                            label={t?.assignedClass || "Assigned Class"}
                            name="assigned_class_id"
                            value={formData.assigned_class_id}
                            onChange={(v) =>
                              handleDropdownChange("assigned_class_id", v)
                            }
                            options={assignedClassOptions}
                            placeholder={t?.selectClass || "Select Class"}
                            required
                            error={errors.assigned_class_id}
                          />
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                          <CustomDropdown
                            label={t?.assignedSection || "Assigned Section"}
                            name="assigned_section"
                            value={
                              formData.assigned_section
                                ? typeof formData.assigned_section === "object"
                                  ? formData.assigned_section.value
                                  : formData.assigned_section
                                : ""
                            }
                            onChange={(v) =>
                              handleDropdownChange("assigned_section", v)
                            }
                            options={assignedSectionOptions}
                            placeholder={
                              !formData.assigned_class_id
                                ? t?.selectClassFirst || "Select Class First"
                                : t?.selectSection || "Select Section"
                            }
                            required
                            disabled={!formData.assigned_class_id}
                            error={errors.assigned_section}
                          />
                        </div>
                      </>
                    )}

                    {/* ── Read-only Assigned Class & Section (show when NOT in seat allocation mode) ── */}
                    {!showSeatAllocationFields && (
                      <>
                        <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                          <CustomTextInput
                            label={t?.assignedClass || "Assigned Class"}
                            name="assigned_class_display"
                            value={formData.assigned_class || "-"}
                            disabled
                            readOnly
                          />
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                          <CustomTextInput
                            label={t?.assignedSection || "Assigned Section"}
                            name="assigned_section_display"
                            value={
                              formData.assigned_section
                                ? typeof formData.assigned_section === "object"
                                  ? formData.assigned_section.value
                                  : formData.assigned_section
                                : "-"
                            }
                            disabled
                            readOnly
                          />
                        </div>
                      </>
                    )}

                    {/* ── Change Section (Optional) for certain statuses ── */}
                    {(formData.current_status === "Seat Allocated" ||
                      formData.current_status === "Interview Scheduled" ||
                      formData.current_status === "Invite Not Sent" ||
                      formData.current_status === "Approved") && (
                      <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                        <CustomDropdown
                          label={
                            t?.changeSectionOptional ||
                            "Change Section (Optional)"
                          }
                          name="assigned_section"
                          value={formData.assigned_section}
                          onChange={(v) =>
                            handleDropdownChange("assigned_section", v)
                          }
                          options={sectionOptions}
                          placeholder={t?.select || "Select"}
                        />
                      </div>
                    )}

                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomTextInput
                        label={t?.currentStatus || "Current Status"}
                        name="current_status"
                        value={formData.current_status || "-"}
                        disabled
                        readOnly
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 px-2 mb-3">
                      <CustomDropdown
                        label={t?.status || "Status"}
                        name="status"
                        value={formData.status || ""}
                        onChange={(v) => handleDropdownChange("status", v)}
                        options={getStatusOptions(formData.current_status)}
                        placeholder={t?.selectStatus || "Select Status"}
                        error={errors.status}
                        disabled={shouldDisableStatus(formData.current_status)}
                      />
                    </div>

                    {/* ── Feedback: shown only when status is Rejected or Approved ── */}
                    {(() => {
                      const selVal = formData.status?.value
                        ? formData.status.value
                        : formData.status;
                      return selVal === "Rejected" || selVal === "Approved";
                    })() && (
                      <div className="col-12 px-2 mb-3">
                        <CustomTextInput
                          label={t?.feedback || "Feedback"}
                          name="feedback"
                          value={formData.feedback}
                          onChange={handleInputChange}
                          onFocus={() => handleTamilInputFocus("feedback")}
                          placeholder={t?.enterHere || "Enter here.."}
                          required
                          error={errors.feedback}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        {/* FOOTER */}
        <Modal.Footer className={styles.modalFooter}>
          <CustomButton
            type="button"
            variant="contained"
            onClick={handleClose}
            fullWidth={false}
            className={styles.backButton}
          >
            {t?.back || "BACK"}
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            onClick={handleUpdateChanges}
            fullWidth={false}
            loading={submitting}
            className={styles.updateButton}
            disabled={formData.current_status === "Withdraw"}
          >
            {t?.updateChanges || "UPDATE CHANGES"}
          </CustomButton>
        </Modal.Footer>
      </Modal>

      {modalState.open && (
        <PopupModal
          open={modalState.open}
          type={modalState.type}
          message={modalState.message}
          onClose={() => setModalState({ open: false, type: "", message: "" })}
        />
      )}
    </>
  );
};

export default SecondaryAdmissionEditModal;
